<script lang="ts">
  import { copyToClipboard } from "@/utils/clipboard";
  import { modalDialog } from "@/actions/modalDialog";
  import { onDestroy, untrack } from "svelte";
  import { get } from "svelte/store";
  import {
    api,
    ApiError,
    type FileContentResponse,
    type FileEntry,
    type FileRevision,
  } from "@/api";
  import { token as tokenStore } from "@/stores/session";
  import ConfigEditor from "@/components/ConfigEditor.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import { confirmModal, promptModal } from "@/stores/modal";
  import { formatBytes } from "@/utils/format";
  import { FILE_TRANSFER_MAX_SIZE } from "@mars-host/shared";

  const MAX_UPLOAD_BYTES = FILE_TRANSFER_MAX_SIZE;

  const {
    serverId,
    disabled = false,
    serverStatus,
  }: { serverId: string; disabled?: boolean; serverStatus?: string } = $props();

  let extractEntry = $state<FileEntry | null>(null);
  let extractDestination = $state("");
  let extractError = $state("");
  const extractBlocked = $derived(serverStatus !== "stopped");
  const invalidExtractDestination = $derived.by(() => {
    const destination = extractDestination.trim();
    const segments = destination.split("/");
    return (
      !destination ||
      destination.startsWith("/") ||
      destination.includes("\\") ||
      segments.some((segment) => !segment || segment === "." || segment === "..") ||
      [...destination].some(
        (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
      )
    );
  });

  let currentPath = $state("");
  let entries = $state<FileEntry[]>([]);
  let loading = $state(true);
  let error = $state("");
  let actionError = $state("");
  let busy = $state<string | null>(null);
  let editorPath = $state<string | null>(null);
  let editorContent = $state("");
  let savedContent = $state("");
  let editorRevision = $state<FileRevision | null>(null);
  let editorSize = $state(0);
  let editorLoading = $state(false);
  let conflict = $state("");
  let notice = $state("");
  let selectedPaths = $state<string[]>([]);
  let fileSearch = $state("");
  let uploadInput: HTMLInputElement | undefined;
  let dragDepth = $state(0);
  let directoryRequest: AbortController | undefined;
  let editorRequest: AbortController | undefined;
  onDestroy(() => {
    directoryRequest?.abort();
    editorRequest?.abort();
  });

  const dirty = $derived(
    editorPath !== null && editorRevision !== null && editorContent !== savedContent,
  );
  const locked = $derived(disabled || busy !== null || loading);
  const breadcrumbs = $derived(currentPath ? currentPath.split("/") : []);
  const visibleEntries = $derived(
    entries.filter((entry) =>
      entry.name.toLocaleLowerCase().includes(fileSearch.trim().toLocaleLowerCase()),
    ),
  );
  const editorBytes = $derived(new Blob([editorContent]).size);
  const largeFileWarning = $derived(editorBytes > 256 * 1024);
  const editorTooLarge = $derived(editorBytes > 512 * 1024);
  const editorReadOnly = $derived.by(() => {
    const unavailableRevision = editorRevision === null || editorTooLarge;
    return unavailableRevision || disabled || busy === "save";
  });
  const savingDisabled = $derived.by(() => {
    const cannotEdit = locked || !dirty;
    return cannotEdit || editorRevision === null || editorTooLarge;
  });
  const editorLanguage = $derived(
    editorPath?.toLowerCase().endsWith(".yaml") || editorPath?.toLowerCase().endsWith(".yml")
      ? "yaml"
      : editorPath?.toLowerCase().endsWith(".json")
        ? "json"
        : editorPath?.toLowerCase().endsWith(".properties") ||
            editorPath?.toLowerCase().endsWith(".conf")
          ? "properties"
          : "text",
  );

  function requireToken(): string {
    const value = get(tokenStore);
    if (!value) {
      throw new Error("нет активной сессии");
    }
    return value;
  }

  function fileKind(
    entry: FileEntry,
  ): "directory" | "config" | "code" | "archive" | "image" | "binary" | "file" {
    if (entry.kind === "directory") {
      return "directory";
    }
    const name = entry.name.toLowerCase();
    if (/\.(yml|yaml|json|toml|ini|conf|properties|env)$/.test(name)) {
      return "config";
    }
    if (/\.(js|ts|svelte|css|html|xml|java|kt|groovy|py|sh|bash|sql|md|txt|log)$/.test(name)) {
      return "code";
    }
    if (/\.(zip|gz|tar|rar|7z|jar|war)$/.test(name)) {
      return "archive";
    }
    if (/\.(png|jpe?g|gif|webp|svg|ico)$/.test(name)) {
      return "image";
    }
    if (entry.editable === false) {
      return "binary";
    }
    return "file";
  }

  function looksBinary(path: string, content: string): boolean {
    const binaryExtension =
      /\.(jar|zip|gz|tar|png|jpe?g|gif|webp|ico|db|sqlite|bin|dat|class)$/i.test(path);
    const controls = [...content].filter((character) => {
      const code = character.charCodeAt(0);
      return code < 9 || (code > 13 && code < 32);
    }).length;
    return binaryExtension || controls > Math.max(2, content.length * 0.02);
  }

  async function loadDirectory(path = currentPath): Promise<void> {
    directoryRequest?.abort();
    const controller = new AbortController();
    directoryRequest = controller;
    const requestedId = serverId;
    loading = true;
    error = "";
    try {
      const result = await api.files(requireToken(), requestedId, path, controller.signal);
      if (controller.signal.aborted || requestedId !== serverId) {
        return;
      }
      if (result.path !== currentPath) {
        fileSearch = "";
      }
      currentPath = result.path;
      entries = result.entries;
      selectedPaths = [];
    } catch (requestError) {
      if (!controller.signal.aborted && requestedId === serverId) {
        error = (requestError as Error).message;
      }
    } finally {
      if (directoryRequest === controller) {
        loading = false;
      }
    }
  }

  const watchedServerId = $derived(serverId);
  $effect(() => {
    void watchedServerId;
    untrack(() => {
      editorRequest?.abort();
      editorLoading = false;
      currentPath = "";
      extractEntry = null;
      editorPath = null;
      editorContent = "";
      savedContent = "";
      editorRevision = null;
      selectedPaths = [];
      void loadDirectory("");
    });
  });

  async function openEntry(entry: FileEntry): Promise<void> {
    if (locked) {
      return;
    }
    if (entry.kind === "file" && entry.editable === false) {
      actionError = "Этот тип файла нельзя редактировать в панели";
      return;
    }
    if (
      dirty &&
      editorPath !== entry.path &&
      !(await confirmModal(
        "Открыть другой файл?",
        "Есть несохранённые изменения. Текущий текст будет потерян.",
      ))
    ) {
      return;
    }
    if (entry.kind === "directory") {
      await loadDirectory(entry.path);
      return;
    }
    editorRequest?.abort();
    const controller = new AbortController();
    editorRequest = controller;
    const requestedId = serverId;
    editorLoading = true;
    editorPath = entry.path;
    editorContent = "";
    savedContent = "";
    editorRevision = null;
    editorSize = 0;
    actionError = "";
    conflict = "";
    notice = "";
    try {
      let result: FileContentResponse = await api.fileContent(
        requireToken(),
        requestedId,
        entry.path,
        0,
        64 * 1024,
        controller.signal,
      );
      if (controller.signal.aborted || requestedId !== serverId) {
        return;
      }
      if (looksBinary(entry.path, result.content)) {
        editorPath = entry.path;
        editorContent =
          "Этот файл слишком большой или имеет неподдерживаемый формат для редактирования в панели.";
        savedContent = editorContent;
        editorRevision = null;
        editorSize = result.size;
        return;
      }
      const chunks = [result.content];
      const initialRevision = result.revision.sha256;
      while (!result.eof) {
        const offset = chunks.reduce((total, chunk) => total + new Blob([chunk]).size, 0);
        result = await api.fileContent(
          requireToken(),
          requestedId,
          entry.path,
          offset,
          64 * 1024,
          controller.signal,
        );
        if (controller.signal.aborted || requestedId !== serverId) {
          return;
        }
        if (result.revision.sha256 !== initialRevision) {
          throw new Error("файл изменился во время чтения");
        }
        chunks.push(result.content);
      }
      editorPath = result.path;
      editorContent = chunks.join("");
      savedContent = editorContent;
      editorRevision = result.revision;
      editorSize = result.size;
    } catch (requestError) {
      if (!controller.signal.aborted && requestedId === serverId) {
        actionError = (requestError as Error).message;
        editorPath = null;
      }
    } finally {
      if (editorRequest === controller) {
        editorLoading = false;
      }
    }
  }

  async function closeEditor(): Promise<void> {
    if (busy !== null) {
      return;
    }
    if (
      dirty &&
      !(await confirmModal(
        "Закрыть файл?",
        "Есть несохранённые изменения. Они будут потеряны.",
        "Закрыть",
      ))
    ) {
      return;
    }
    editorRequest?.abort();
    editorLoading = false;
    editorPath = null;
    editorContent = "";
    savedContent = "";
    editorRevision = null;
    editorSize = 0;
    conflict = "";
  }

  async function createFile(): Promise<void> {
    const name = await promptModal(
      "Новый файл",
      "",
      "Имя файла (например, server.properties)",
      "Создать",
    );
    if (!name?.trim() || locked) {
      return;
    }
    const cleanName = name.trim();
    const destination = currentPath ? `${currentPath}/${cleanName}` : cleanName;
    busy = "mkfile";
    actionError = "";
    notice = "";
    try {
      const result = await api.writeFile(requireToken(), serverId, {
        path: destination,
        content: "",
        revision: null,
      });
      notice = "Файл создан";
      await loadDirectory();
      editorPath = destination;
      editorContent = "";
      savedContent = "";
      editorRevision = result.revision;
      editorSize = 0;
    } catch (requestError) {
      actionError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  async function createDirectory(): Promise<void> {
    const name = await promptModal("Новая папка", "", "Имя папки", "Создать");
    if (!name?.trim() || locked) {
      return;
    }
    busy = "mkdir";
    actionError = "";
    try {
      await api.createDirectory(
        requireToken(),
        serverId,
        currentPath ? `${currentPath}/${name.trim()}` : name.trim(),
      );
      notice = "Папка создана";
      await loadDirectory();
    } catch (requestError) {
      actionError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  async function uploadFiles(event: Event): Promise<void> {
    const input = event.currentTarget as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    await uploadSelectedFiles(files);
  }

  function hasDraggedFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes("Files");
  }

  function dragEnter(event: DragEvent): void {
    if (!hasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepth++;
  }

  function dragOver(event: DragEvent): void {
    if (!hasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = locked ? "none" : "copy";
    }
  }

  function dragLeave(event: DragEvent): void {
    if (!hasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
  }

  async function dropFiles(event: DragEvent): Promise<void> {
    if (!hasDraggedFiles(event)) {
      return;
    }
    event.preventDefault();
    dragDepth = 0;
    if (locked) {
      return;
    }
    const items = Array.from(event.dataTransfer?.items ?? []);
    if (items.some((item) => item.webkitGetAsEntry?.()?.isDirectory)) {
      actionError = "Перетащите отдельные файлы. Загрузка папок пока не поддерживается.";
      return;
    }
    await uploadSelectedFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  async function uploadSelectedFiles(files: File[]): Promise<void> {
    if (!files.length || locked) {
      return;
    }
    const tooLarge = files.find((file) => file.size > MAX_UPLOAD_BYTES);
    if (tooLarge) {
      actionError = `«${tooLarge.name}» больше 8 МБ. Выберите файл меньшего размера.`;
      return;
    }
    const invalidName = files.find((file) => {
      const missingOrRelativeName = !file.name || file.name === "." || file.name === "..";
      return missingOrRelativeName || /[\\/\0]/.test(file.name);
    });
    if (invalidName) {
      actionError = "Имя файла содержит недопустимые символы";
      return;
    }
    const names = files.map((file) => file.name.toLocaleLowerCase());
    if (new Set(names).size !== names.length) {
      actionError = "В списке есть файлы с одинаковыми именами. Удалите дубликат перед загрузкой.";
      return;
    }
    const directoryCollision = files.find((file) =>
      entries.some((entry) => entry.kind === "directory" && entry.name === file.name),
    );
    if (directoryCollision) {
      actionError = `«${directoryCollision.name}» — папка. Выберите другое имя файла.`;
      return;
    }
    busy = "upload";
    actionError = "";
    notice = "";
    let uploaded = 0;
    try {
      for (const [index, file] of files.entries()) {
        notice = `Загружаем файл ${index + 1} из ${files.length}: ${file.name}`;
        const destination = currentPath ? `${currentPath}/${file.name}` : file.name;
        await api.uploadFile(requireToken(), serverId, destination, file);
        uploaded++;
      }
      notice = `Загружено файлов: ${files.length}`;
      selectedPaths = [];
      await loadDirectory();
    } catch (requestError) {
      actionError =
        uploaded > 0
          ? `Загружено ${uploaded} из ${files.length} файлов. ${(requestError as Error).message}`
          : (requestError as Error).message;
      notice = "";
      if (uploaded > 0) {
        await loadDirectory();
      }
    } finally {
      busy = null;
    }
  }

  async function downloadEntry(entry: FileEntry): Promise<void> {
    if (locked || entry.kind !== "file") {
      return;
    }
    if (entry.size > MAX_UPLOAD_BYTES) {
      actionError = `Файл больше 8 МБ и пока не может быть скачан через панель`;
      return;
    }
    busy = `download:${entry.path}`;
    actionError = "";
    notice = "";
    try {
      const result = await api.downloadFile(requireToken(), serverId, entry.path);
      const binary = atob(result.content);
      const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes]));
      const link = document.createElement("a");
      link.href = url;
      link.download = result.path.split("/").pop() ?? entry.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
      notice = `Файл «${link.download}» скачан`;
    } catch (requestError) {
      actionError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  function openExtract(entry: FileEntry): void {
    if (locked || extractBlocked || entry.kind !== "file" || !/\.zip$/i.test(entry.name)) {
      return;
    }
    extractEntry = entry;
    extractDestination = entry.path.replace(/\.zip$/i, "");
    extractError = "";
  }

  function closeExtract(): void {
    if (busy !== "extract") {
      extractEntry = null;
    }
  }

  async function extractArchive(): Promise<void> {
    if (!extractEntry || locked || extractBlocked || invalidExtractDestination) {
      return;
    }
    const requestedId = serverId;
    const archive = extractEntry.path;
    const destination = extractDestination.trim();
    busy = "extract";
    extractError = "";
    actionError = "";
    notice = "";
    try {
      const result = await api.extractArchive(requireToken(), requestedId, archive, destination);
      if (requestedId !== serverId) {
        return;
      }
      extractEntry = null;
      notice = `Архив распакован в «${result.path}»: файлов ${result.files}, ${formatBytes(result.bytes)}`;
      await loadDirectory();
    } catch (requestError) {
      if (requestedId === serverId) {
        extractError = (requestError as Error).message;
      }
    } finally {
      busy = null;
    }
  }

  async function renameEntry(entry: FileEntry): Promise<void> {
    const name = await promptModal("Переименовать", entry.name, "Новое имя", "Переименовать");
    if (!name?.trim() || name.trim() === entry.name || locked) {
      return;
    }
    const destination = currentPath ? `${currentPath}/${name.trim()}` : name.trim();
    busy = `rename:${entry.path}`;
    actionError = "";
    try {
      await api.moveFile(requireToken(), serverId, entry.path, destination);
      if (editorPath === entry.path) {
        editorPath = destination;
      }
      notice = "Переименовано";
      await loadDirectory();
    } catch (requestError) {
      actionError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  async function deleteEntry(entry: FileEntry): Promise<void> {
    if (
      locked ||
      !(await confirmModal(
        "Удалить элемент?",
        entry.kind === "directory"
          ? `Удалить папку «${entry.name}» вместе со всем содержимым?`
          : `Удалить «${entry.name}»?`,
        "Удалить",
      ))
    ) {
      return;
    }
    busy = `delete:${entry.path}`;
    actionError = "";
    try {
      await api.deleteFile(requireToken(), serverId, entry.path);
      if (editorPath === entry.path || editorPath?.startsWith(`${entry.path}/`)) {
        editorPath = null;
        editorContent = "";
        savedContent = "";
        editorRevision = null;
      }
      notice = "Удалено";
      await loadDirectory();
    } catch (requestError) {
      actionError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  async function save(): Promise<void> {
    if (!editorPath || !editorRevision || locked || !dirty) {
      return;
    }
    if (editorBytes > 512 * 1024) {
      actionError = "Файл больше 512 КБ и не может быть сохранён через web-редактор";
      return;
    }
    if (
      largeFileWarning &&
      !(await confirmModal(
        "Сохранить большой файл?",
        "Файл больше 256 КБ. Сохранение может занять время.",
        "Продолжить",
      ))
    ) {
      return;
    }
    const contentToSave = editorContent;
    busy = "save";
    actionError = "";
    conflict = "";
    notice = "";
    try {
      const result = await api.writeFile(requireToken(), serverId, {
        path: editorPath,
        content: contentToSave,
        revision: editorRevision,
      });
      editorRevision = result.revision;
      editorSize = result.size;
      savedContent = contentToSave;
      notice = "Файл сохранён";
    } catch (requestError) {
      if (
        requestError instanceof ApiError &&
        requestError.status === 409 &&
        requestError.message.startsWith("файл изменился на сервере")
      ) {
        conflict =
          "Файл изменился на сервере. Ваши изменения остались в редакторе. Скопируйте свой текст перед загрузкой актуальной версии.";
      } else {
        actionError = (requestError as Error).message;
      }
    } finally {
      busy = null;
    }
  }

  function goUp(): void {
    if (!currentPath) {
      return;
    }
    const parts = currentPath.split("/");
    parts.pop();
    void loadDirectory(parts.join("/"));
  }

  function goToRoot(): void {
    void loadDirectory("");
  }

  function toggleSelect(path: string): void {
    if (selectedPaths.includes(path)) {
      selectedPaths = selectedPaths.filter((p) => p !== path);
    } else {
      selectedPaths = [...selectedPaths, path];
    }
  }

  function toggleSelectAll(): void {
    const visiblePaths = visibleEntries.map((entry) => entry.path);
    if (visiblePaths.length > 0 && visiblePaths.every((path) => selectedPaths.includes(path))) {
      selectedPaths = selectedPaths.filter((path) => !visiblePaths.includes(path));
    } else {
      selectedPaths = [...new Set([...selectedPaths, ...visiblePaths])];
    }
  }

  async function bulkDelete(): Promise<void> {
    if (locked || selectedPaths.length === 0) {
      return;
    }
    const count = selectedPaths.length;
    if (
      !(await confirmModal(
        "Удалить выбранное?",
        `Удалить выбранные элементы (${count} шт.)? Папки будут удалены вместе с содержимым.`,
        "Удалить",
      ))
    ) {
      return;
    }
    busy = "bulk-delete";
    actionError = "";
    try {
      for (const p of selectedPaths) {
        await api.deleteFile(requireToken(), serverId, p);
        if (editorPath === p || editorPath?.startsWith(`${p}/`)) {
          editorPath = null;
          editorContent = "";
          savedContent = "";
          editorRevision = null;
        }
      }
      notice = `Удалено элементов: ${count}`;
      selectedPaths = [];
      await loadDirectory();
    } catch (requestError) {
      actionError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  async function bulkMove(): Promise<void> {
    if (locked || selectedPaths.length === 0) {
      return;
    }
    const count = selectedPaths.length;
    const targetFolder = await promptModal(
      "Переместить выбранные",
      currentPath,
      "Целевая папка (от корня, пусто для корня)",
      "Переместить",
    );
    if (targetFolder === null || locked) {
      return;
    }
    const cleanTarget = targetFolder.trim().replace(/^\/+|\/+$/g, "");
    busy = "bulk-move";
    actionError = "";
    try {
      for (const p of selectedPaths) {
        const fileName = p.split("/").pop() ?? "";
        const dest = cleanTarget ? `${cleanTarget}/${fileName}` : fileName;
        if (p !== dest) {
          await api.moveFile(requireToken(), serverId, p, dest);
        }
      }
      notice = `Перемещено элементов: ${count}`;
      selectedPaths = [];
      await loadDirectory();
    } catch (requestError) {
      actionError = (requestError as Error).message;
    } finally {
      busy = null;
    }
  }

  async function copyEditorContent(): Promise<void> {
    if (await copyToClipboard(editorContent)) {
      notice = "Ваш текст скопирован";
    } else {
      actionError = "Не удалось скопировать текст. Выделите его в редакторе и скопируйте вручную.";
    }
  }

  async function reloadEditor(): Promise<void> {
    if (!editorPath || locked) {
      return;
    }
    if (
      dirty &&
      !(await confirmModal(
        "Загрузить версию сервера?",
        "Ваши несохранённые изменения будут заменены содержимым файла на сервере. Сначала скопируйте свой текст.",
        "Загрузить",
      ))
    ) {
      return;
    }
    if (!editorPath || locked) {
      return;
    }
    await openEntry({
      path: editorPath,
      name: editorPath.split("/").at(-1) ?? editorPath,
      kind: "file",
      size: editorSize,
      mtimeMs: editorRevision?.mtimeMs ?? 0,
    });
  }

  function refreshDirectory(): void {
    void loadDirectory();
  }
</script>

<section
  class="panel-card file-manager"
  class:drag-active={dragDepth > 0 && !locked}
  aria-labelledby="files-title"
  ondragenter={dragEnter}
  ondragover={dragOver}
  ondragleave={dragLeave}
  ondrop={dropFiles}
>
  {#if dragDepth > 0 && !locked}
    <div class="file-drop-overlay" role="status">
      <strong>Отпустите файлы для загрузки</strong>
      <span>В текущую папку: {currentPath || "корень сервера"}</span>
    </div>
  {/if}
  <header class="panel-heading file-heading">
    <div>
      <p class="eyebrow">Файлы сервера</p>
      <h2 id="files-title">Данные и конфигурация</h2>
    </div>
    <div class="file-heading-actions">
      <button class="file-action" type="button" disabled={locked} onclick={createFile}>
        {busy === "mkfile" ? "Создаём…" : "Новый файл"}
      </button>
      <button class="file-action" type="button" disabled={locked} onclick={createDirectory}>
        {busy === "mkdir" ? "Создаём…" : "Новая папка"}
      </button>
      <button
        class="file-action"
        type="button"
        disabled={locked}
        onclick={() => uploadInput?.click()}
      >
        {busy === "upload" ? "Загружаем…" : "Загрузить файлы"}
      </button>
      <input
        bind:this={uploadInput}
        class="file-upload-input"
        type="file"
        multiple
        aria-label="Выберите файлы для загрузки"
        onchange={uploadFiles}
        disabled={locked}
      />
    </div>
  </header>

  {#if disabled}
    <div class="file-locked">
      Файлы временно недоступны. Дождитесь завершения текущей операции с сервером.
    </div>
  {/if}
  {#if error}<div class="file-error" role="alert">{error}</div>{/if}
  {#if actionError && !editorPath}<div class="file-error" role="alert">{actionError}</div>{/if}
  {#if extractBlocked && entries.some((entry) => entry.kind === "file" && /\.zip$/i.test(entry.name))}
    <div class="file-notice">Распаковка ZIP доступна после остановки сервера.</div>
  {/if}
  {#if notice && !editorPath}<div class="file-notice" role="status">{notice}</div>{/if}

  <div class="file-toolbar">
    <button
      type="button"
      disabled={locked || visibleEntries.length === 0}
      onclick={toggleSelectAll}
    >
      {visibleEntries.length > 0 &&
      visibleEntries.every((entry) => selectedPaths.includes(entry.path))
        ? "Снять выбор"
        : "Выбрать найденные"}
    </button>
    <button type="button" disabled={locked || !currentPath} onclick={goUp}>← Вверх</button>
    <div class="file-breadcrumbs" aria-label="Текущий каталог">
      <button type="button" disabled={locked} onclick={goToRoot}>корень</button>
      {#each breadcrumbs as segment, index (index)}
        <span aria-hidden="true">/</span>
        <button
          type="button"
          disabled={locked}
          onclick={() => void loadDirectory(breadcrumbs.slice(0, index + 1).join("/"))}
          >{segment}</button
        >
      {/each}
    </div>
    <button type="button" disabled={locked} onclick={refreshDirectory}>Обновить</button>
  </div>

  <div class="file-search-row">
    <label class="visually-hidden" for="file-search">Поиск в текущей папке</label>
    <TextInput
      id="file-search"
      type="search"
      density="compact"
      flexible
      bind:value={fileSearch}
      placeholder="Найти файл или папку"
      disabled={loading}
    />
    <span>{visibleEntries.length} из {entries.length}</span>
  </div>

  {#if selectedPaths.length > 0}
    <div class="file-bulk-bar">
      <span>Выбрано: <strong>{selectedPaths.length}</strong></span>
      <div class="file-bulk-actions">
        <button class="file-bulk-btn" type="button" disabled={locked} onclick={bulkMove}>
          {busy === "bulk-move" ? "Перемещаем…" : "Переместить"}
        </button>
        <button class="file-bulk-btn danger" type="button" disabled={locked} onclick={bulkDelete}>
          {busy === "bulk-delete" ? "Удаляем…" : "Удалить"}
        </button>
      </div>
    </div>
  {/if}

  <div class="file-layout">
    <div class="file-list" aria-label="Файлы и папки">
      {#if loading}
        <div class="file-empty">
          <span class="loading-orbit"></span>
          <p>Читаем каталог…</p>
        </div>
      {:else if entries.length === 0}
        <div class="file-empty">
          <p>Каталог пуст</p>
          <small>Создайте файл или папку либо загрузите файл с устройства.</small>
        </div>
      {:else if visibleEntries.length === 0}
        <div class="file-empty">
          <p>Совпадений нет</p>
          <small>Измените запрос или очистите поиск.</small>
        </div>
      {:else}
        {#each visibleEntries as entry (entry.path)}
          {@const kind = fileKind(entry)}
          <article
            class:selected={editorPath === entry.path || selectedPaths.includes(entry.path)}
            class="file-card"
          >
            <label class="file-card-select" title={`Выбрать ${entry.name}`}>
              <input
                type="checkbox"
                aria-label={`Выбрать ${entry.name}`}
                checked={selectedPaths.includes(entry.path)}
                onchange={() => toggleSelect(entry.path)}
                disabled={locked}
              />
            </label>
            <button
              class="file-open"
              type="button"
              disabled={locked || (entry.kind === "file" && entry.editable === false)}
              onclick={() => void openEntry(entry)}
            >
              <span class="file-card-head">
                <span class="file-icon" data-kind={kind} aria-hidden="true">
                  <svg viewBox="0 0 48 48" focusable="false">
                    {#if kind === "directory"}
                      <path
                        d="M5 13.5A3.5 3.5 0 0 1 8.5 10h11l4 4H39.5A3.5 3.5 0 0 1 43 17.5v17A3.5 3.5 0 0 1 39.5 38h-31A3.5 3.5 0 0 1 5 34.5z"
                      />
                      <path class="file-icon-detail" d="M5.5 18h37" />
                    {:else}
                      <path d="M12 5h17l8 8v30H12z" />
                      <path class="file-icon-detail" d="M29 5v9h8" />
                      {#if kind === "config"}
                        <circle class="file-icon-symbol" cx="25" cy="29" r="6" />
                        <circle class="file-icon-symbol-center" cx="25" cy="29" r="2" />
                        <path
                          class="file-icon-symbol"
                          d="M25 20v3m0 12v3m9-9h-3m-12 0h-3m15.4-6.4-2.1 2.1m-8.6 8.6-2.1 2.1m12.8 0-2.1-2.1m-8.6-8.6-2.1-2.1"
                        />
                      {:else if kind === "code"}
                        <path class="file-icon-symbol" d="m23 23-5 5 5 5m8-10 5 5-5 5m-3-8-3 11" />
                      {:else if kind === "archive"}
                        <path
                          class="file-icon-symbol"
                          d="M19 22h17v13H19zM24 22v4h7v-4m-4 4v4m-4 0h8m-8 4h8"
                        />
                      {:else if kind === "image"}
                        <path class="file-icon-symbol" d="M18 21h18v14H18z" />
                        <circle class="file-icon-symbol-fill" cx="31.5" cy="25.5" r="2" />
                        <path class="file-icon-symbol-fill" d="m19 33 5-5 4 4 3-3 5 5v1H19z" />
                      {:else if kind === "binary"}
                        <rect
                          class="file-icon-symbol"
                          x="19"
                          y="22"
                          width="17"
                          height="13"
                          rx="1"
                        />
                        <path
                          class="file-icon-symbol"
                          d="M23 19v3m5-3v3m5-3v3m-10 13v3m5-3v3m5-3v3m-17-10h3m-3 5h3m17-5h3m-3 5h3"
                        />
                      {:else}
                        <path class="file-icon-detail" d="M19 25h13M19 30h13M19 35h8" />
                      {/if}
                    {/if}
                  </svg>
                </span>
              </span>
              <span class="file-card-copy"
                ><strong>{entry.name}</strong><small
                  >{entry.kind === "directory"
                    ? "Папка"
                    : entry.editable === false
                      ? "Недоступен"
                      : formatBytes(entry.size)}</small
                ></span
              >
            </button>
            <div class="file-card-actions">
              <button
                type="button"
                title="Переименовать"
                aria-label={`Переименовать ${entry.name}`}
                disabled={locked || (entry.kind === "file" && entry.editable === false)}
                onclick={() => void renameEntry(entry)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
                  ><path d="m14 5 5 5M4 20l4.2-.8L19 8.4a2.1 2.1 0 0 0-3-3L5.2 16.2 4 20Z" /></svg
                >
              </button>
              {#if entry.kind === "file"}
                <button
                  type="button"
                  title={entry.size > MAX_UPLOAD_BYTES ? "Файл больше 8 МБ" : "Скачать"}
                  aria-label={`Скачать ${entry.name}`}
                  disabled={locked || entry.size > MAX_UPLOAD_BYTES}
                  onclick={() => void downloadEntry(entry)}
                >
                  {#if busy === `download:${entry.path}`}<span aria-hidden="true">…</span
                    >{:else}<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
                      ><path d="M12 3v11m-4-4 4 4 4-4M4 17v3h16v-3" /></svg
                    >{/if}
                </button>
              {/if}
              {#if entry.kind === "file" && /\.zip$/i.test(entry.name)}
                <button
                  type="button"
                  title={extractBlocked ? "Для распаковки остановите сервер" : "Распаковать ZIP"}
                  aria-label={`Распаковать ${entry.name}`}
                  disabled={locked || extractBlocked}
                  onclick={() => openExtract(entry)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
                    ><path d="M4 9h16v11H4zM3 4h18v5H3zM12 11v6m-3-3 3 3 3-3" /></svg
                  >
                </button>
              {/if}
              <button
                type="button"
                title="Удалить"
                aria-label={`Удалить ${entry.name}`}
                disabled={locked}
                onclick={() => void deleteEntry(entry)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
                  ><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg
                >
              </button>
            </div>
          </article>
        {/each}
      {/if}
    </div>
  </div>

  {#if extractEntry}
    <dialog
      class="extract-dialog"
      use:modalDialog={{
        onDismiss: closeExtract,
        canDismiss: () => busy !== "extract",
        initialFocus: "input",
      }}
      aria-labelledby="extract-title"
    >
      <form
        onsubmit={(event) => {
          event.preventDefault();
          void extractArchive();
        }}
      >
        <h2 id="extract-title">Распаковать ZIP</h2>
        <p class="extract-source">{extractEntry.path}</p>
        <p>
          Архив будет распакован в новую папку. Существующие файлы не перезаписываются. Исходный
          архив сохранится.
        </p>
        <label for="extract-destination">Новая папка от корня сервера</label>
        <TextInput
          id="extract-destination"
          bind:value={extractDestination}
          disabled={busy === "extract"}
          autocomplete="off"
        />
        {#if extractBlocked}<p class="file-error" role="alert">
            Для распаковки остановите сервер.
          </p>{/if}
        {#if extractError}<p class="file-error" role="alert">{extractError}</p>{/if}
        <div class="extract-actions">
          <button
            class="ghost-button"
            type="button"
            disabled={busy === "extract"}
            onclick={closeExtract}>Отмена</button
          >
          <button
            class="button"
            type="submit"
            disabled={locked || extractBlocked || invalidExtractDestination}
            >{busy === "extract" ? "Распаковываем…" : "Распаковать"}</button
          >
        </div>
      </form>
    </dialog>
  {/if}

  {#if editorPath || editorLoading}
    <dialog
      class="file-editor"
      use:modalDialog={{
        onDismiss: closeEditor,
        canDismiss: () => busy === null,
        initialFocus: ".editor-close",
      }}
      aria-labelledby="editor-title"
    >
      <header class="editor-heading">
        <div>
          <span class="eyebrow">Редактор</span><strong id="editor-title"
            >{editorPath ?? "Открываем файл"}{dirty ? " · есть изменения" : ""}</strong
          >
        </div>
        <button
          class="ghost-button editor-close"
          type="button"
          aria-label="Закрыть редактор"
          disabled={busy !== null}
          onclick={closeEditor}>Закрыть</button
        >
      </header>
      {#if actionError}<div class="file-error" role="alert">{actionError}</div>{/if}
      {#if conflict}
        <div class="file-conflict" role="alert">
          <p>{conflict}</p>
          <div class="conflict-actions">
            <button class="ghost-button" type="button" onclick={copyEditorContent}
              >Скопировать мой текст</button
            >
            <button class="ghost-button" type="button" onclick={reloadEditor}
              >Загрузить версию сервера</button
            >
          </div>
        </div>
      {/if}
      {#if notice}<div class="file-notice" role="status">{notice}</div>{/if}
      {#if editorLoading}
        <div class="file-empty">
          <span class="loading-orbit"></span>
          <p>Открываем файл…</p>
        </div>
      {:else}
        <ConfigEditor
          bind:value={editorContent}
          language={editorLanguage}
          readonly={editorReadOnly}
          ariaLabel={`Содержимое ${editorPath}`}
        />
        <footer class="editor-footer">
          <small>
            {#if editorTooLarge}
              Файл больше 512 КБ: сохранение недоступно
            {:else if largeFileWarning}
              Большой файл: перед сохранением будет подтверждение
            {:else}
              Перед сохранением проверяем, не изменился ли файл
            {/if}
          </small>
          <div class="editor-actions">
            <button
              class="ghost-button"
              type="button"
              disabled={busy !== null}
              onclick={closeEditor}>Закрыть</button
            >
            <button class="button" type="button" disabled={savingDisabled} onclick={save}
              >{busy === "save" ? "Сохраняем…" : "Сохранить"}</button
            >
          </div>
        </footer>
      {/if}
    </dialog>
  {/if}
</section>

<style>
  .file-manager {
    position: relative;
    margin-top: 1.5rem;
    overflow: hidden;
  }
  .file-manager.drag-active {
    outline: 2px dashed var(--mars-light);
    outline-offset: -4px;
  }
  .file-drop-overlay {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 1.5rem;
    text-align: center;
    overflow-wrap: anywhere;
    color: var(--mars-light);
    background: rgb(15 20 32 / 92%);
    pointer-events: none;
  }
  .file-heading {
    align-items: center;
  }
  .file-heading h2 {
    margin-bottom: 0;
  }
  .file-heading-actions {
    display: flex;
    gap: 0.85rem;
    align-items: center;
  }
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .file-upload-input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .file-action,
  .file-toolbar button {
    border: 0;
    color: var(--mars-light);
    background: transparent;
    font-size: 0.85rem;
  }
  .file-action:hover:not(:disabled),
  .file-toolbar button:hover:not(:disabled) {
    color: var(--cream);
  }
  .file-error,
  .file-conflict,
  .file-notice,
  .file-locked {
    padding: 0.7rem 1.5rem;
    border-top: 1px solid var(--line);
    font-size: 0.72rem;
  }
  .file-error {
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.08);
  }
  .conflict-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .file-conflict p {
    margin: 0 0 0.75rem;
    line-height: 1.5;
  }
  .file-conflict {
    color: var(--amber);
    background: rgb(232 162 77 / 0.08);
  }
  .file-notice {
    color: var(--green);
    background: rgb(154 189 143 / 0.06);
  }
  .file-locked {
    color: var(--dim);
  }
  .file-toolbar {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-height: 2.8rem;
    padding: 0.5rem 1.5rem;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
    color: var(--dim);
    font: 500 0.65rem/1 var(--font-display);
    text-transform: uppercase;
  }
  .file-search-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
    border-bottom: 1px solid var(--line);
    color: var(--muted);
    font-size: 0.75rem;
  }
  .file-breadcrumbs {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 0.45rem;
    overflow: hidden;
  }
  .file-breadcrumbs button {
    min-width: 0;
    overflow: hidden;
    padding: 0;
    border: 0;
    color: var(--dim);
    background: transparent;
    font: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .file-breadcrumbs button:hover:not(:disabled) {
    color: var(--cream);
  }
  .file-layout {
    min-height: 26rem;
  }
  .file-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(10.5rem, 1fr));
    gap: 0.7rem;
    padding: 1rem;
    background: rgb(0 0 0 / 0.12);
  }
  .file-card {
    position: relative;
    min-width: 0;
    overflow: hidden;
    border: 1px solid var(--line);
    border-radius: 0.35rem;
    background: rgb(241 225 198 / 0.025);
    transition:
      border-color 140ms ease,
      background 140ms ease,
      transform 140ms ease;
  }
  .file-card:hover {
    border-color: var(--line-strong);
    background: rgb(241 225 198 / 0.06);
    transform: translateY(-1px);
  }
  .file-card.selected {
    border-color: var(--mars-light);
    background: rgb(214 75 50 / 0.1);
  }
  .file-open {
    display: block;
    width: 100%;
    min-width: 0;
    padding: 0.85rem 0.85rem 0.75rem;
    border: 0;
    color: var(--cream);
    background: transparent;
    text-align: left;
  }
  .file-open:hover:not(:disabled) {
    background: transparent;
  }
  .file-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 4.6rem;
  }
  .file-icon {
    position: relative;
    display: grid;
    width: 3.6rem;
    height: 3.6rem;
    place-items: center;
    color: var(--mars-light);
  }
  .file-icon svg {
    width: 100%;
    height: 100%;
    fill: rgb(214 75 50 / 0.18);
    stroke: currentColor;
    stroke-width: 1.7;
    stroke-linejoin: round;
  }
  .file-icon[data-kind="directory"] {
    color: var(--amber);
  }
  .file-icon[data-kind="directory"] svg {
    fill: rgb(232 162 77 / 0.2);
  }
  .file-icon-detail {
    fill: none;
    stroke-width: 1.4;
  }
  .file-icon-symbol {
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .file-icon-symbol-center {
    fill: var(--ink-deep);
    stroke-width: 1.5;
  }
  .file-icon-symbol-fill {
    fill: currentColor;
    stroke: none;
  }
  .file-card-copy {
    display: grid;
    min-width: 0;
    gap: 0.3rem;
  }
  .file-card-copy strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.76rem;
  }
  .file-open small,
  .file-card-actions button,
  .file-toolbar,
  .editor-footer small {
    color: var(--dim);
    font-size: 0.8rem;
  }
  .file-card-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.4rem;
    padding: 0 0.6rem 0.6rem;
    opacity: 1;
    transition: opacity 140ms ease;
  }
  .file-card:hover .file-card-actions,
  .file-card:focus-within .file-card-actions {
    opacity: 1;
  }
  .file-card-actions button {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 0.2rem;
    background: rgb(8 7 6 / 45%);
  }
  .file-card-actions button:hover:not(:disabled) {
    border-color: var(--line-strong);
    color: var(--mars-light);
  }
  .file-card-actions button svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
    vertical-align: middle;
  }
  .extract-dialog {
    width: min(32rem, calc(100vw - 2rem));
    max-height: calc(100dvh - 2rem);
    box-sizing: border-box;
    overflow: auto;
    border: 1px solid var(--line-strong);
    color: var(--cream);
    background: var(--ink-deep);
    padding: 1.25rem;
  }
  .extract-dialog form {
    display: grid;
    gap: 1rem;
    min-width: 0;
  }
  .extract-dialog h2,
  .extract-dialog p {
    margin: 0;
  }
  .extract-dialog p,
  .extract-dialog label {
    font-size: 0.85rem;
    line-height: 1.5;
  }
  .extract-source {
    overflow-wrap: anywhere;
    color: var(--dim);
  }
  .extract-actions {
    display: flex;
    gap: 0.7rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .file-card-actions {
    flex-wrap: wrap;
  }
  .extract-dialog::backdrop,
  .file-editor::backdrop {
    background: rgb(8 7 6 / 72%);
    backdrop-filter: blur(5px);
  }
  .file-editor {
    position: fixed;
    top: 50%;
    left: 50%;
    display: flex;
    width: min(72rem, calc(100vw - 2rem));
    height: min(82dvh, 56rem);
    max-height: calc(100dvh - 2rem);
    min-width: 0;
    flex-direction: column;
    margin: 0;
    padding: 0;
    transform: translate(-50%, -50%);
    border: 1px solid var(--line-strong);
    border-radius: 2px;
    color: var(--cream);
    background: var(--ink-deep);
    box-shadow: 0 1.5rem 5rem rgb(0 0 0 / 35%);
  }
  :global(.file-editor .config-editor) {
    flex: 1;
    min-height: 0;
  }
  :global(.file-editor .config-editor pre),
  :global(.file-editor .config-editor textarea) {
    min-height: 0;
  }
  .editor-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--line);
  }
  .editor-heading div {
    display: grid;
    min-width: 0;
    gap: 0.4rem;
  }
  .editor-heading strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: 500 0.75rem/1 var(--font-display);
  }
  .editor-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--line);
  }
  .editor-actions {
    display: flex;
    align-items: center;
    gap: 0.7rem;
  }
  .editor-footer .button {
    min-height: 2.5rem;
    padding-inline: 1rem;
  }
  .file-empty {
    display: grid;
    min-height: 12rem;
    place-content: center;
    justify-items: center;
    gap: 0.8rem;
    padding: 2rem;
    color: var(--dim);
    text-align: center;
  }
  .file-empty p,
  .file-empty small {
    margin: 0;
    font-size: 0.75rem;
  }
  .file-empty small {
    max-width: 25ch;
    line-height: 1.5;
  }
  @media (max-width: 720px) {
    .file-list {
      grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
      border-bottom: 1px solid var(--line);
      max-height: 30rem;
      overflow: auto;
    }
    .file-editor {
      top: 0;
      left: 0;
      width: 100vw;
      max-width: none;
      height: 100dvh;
      max-height: 100dvh;
      transform: none;
      border: 0;
      border-radius: 0;
    }
  }
  @media (max-width: 520px) {
    .file-heading {
      align-items: flex-start;
      flex-direction: column;
      gap: 0.5rem;
    }
    .file-heading-actions {
      width: 100%;
      justify-content: flex-start;
      flex-wrap: wrap;
      gap: 0.4rem 0.75rem;
    }
    .file-toolbar {
      gap: 0.55rem;
      padding-inline: 1rem;
    }
    .file-toolbar > button:first-child {
      display: none;
    }
    .file-search-row {
      padding-inline: 1rem;
    }
    .editor-footer {
      align-items: flex-start;
      flex-direction: column;
    }
    .editor-footer .button {
      width: 100%;
    }
  }

  .file-bulk-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1.5rem;
    background: rgb(214 75 50 / 0.12);
    border-bottom: 1px solid var(--line-strong);
    font-size: 0.72rem;
  }
  .file-bulk-actions {
    display: flex;
    gap: 0.5rem;
  }
  .file-bulk-btn {
    border: 1px solid var(--line-strong);
    border-radius: 0.2rem;
    background: rgb(241 225 198 / 0.08);
    color: var(--cream);
    font-size: 0.68rem;
    padding: 0.25rem 0.6rem;
    cursor: pointer;
  }
  .file-bulk-btn:hover:not(:disabled) {
    background: rgb(241 225 198 / 0.16);
  }
  .file-bulk-btn.danger {
    border-color: var(--mars-light);
    color: var(--mars-light);
  }
  .file-bulk-btn.danger:hover:not(:disabled) {
    background: rgb(214 75 50 / 0.2);
  }
  .file-card-select {
    position: absolute;
    top: 0.55rem;
    left: 0.55rem;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .file-card-select input {
    cursor: pointer;
    accent-color: var(--mars-light);
  }
</style>
